/**
 * PrintStationPreviews — the product previews on a print-station garment card.
 *
 * ONE STRIP, PRODUCT IMAGES ONLY, NO HEADINGS.
 *
 * The printer is looking at the garment they are about to print on and the
 * design that is supposed to end up on it. That is what these tiles show: the
 * design rendered ON the shirt, or the customer's own combined preview. The
 * card used to carry a second group of bare print files on a transparency
 * checkerboard, first and largest, under a "Print artwork" heading. Brett
 * struck all of it off the screenshot on 2026-09-21: "No artwork previews ...
 * just show the image preview on the garment, that is what we need to see."
 *
 * So the entry policy is the one the other three garment stations use
 * (`buildFinishedGarmentPreviewEntries`), and everything that existed to keep
 * two disjoint groups apart — the group discriminator, the two key
 * namespaces, the group argument on every callback — is gone with them. One
 * strip means one namespace, so a tile's key is just
 * `productionPreviewKey(garmentUnitId, kind)` like everywhere else.
 *
 * ONE DEVIATION, AND IT IS THIS STATION'S ALONE. The shared builder is
 * SOURCE-driven: it emits a Front tile because a front mockup exists. But the
 * mockup mirror is keyed per (org, sku, purpose), NOT per printed side, so an
 * org that mapped both metafields has both mockups on every SKU — back-only
 * jobs included. Unfiltered, this card would caption a tile "Front" directly
 * under a banner reading "Back only", which is two answers to the one question
 * this screen exists to ask. So Print, and only Print, drops a side tile for a
 * side this job does not print (`buildPrintStationPreviewEntries` below).
 * Catching, Pressing and Packing stay unfiltered on purpose: their operator is
 * checking the FINISHED garment, so both of its sides are worth seeing
 * whichever one was printed here.
 *
 * NOTHING HERE IS PRINTABLE, and nothing here decides what gets printed. Every
 * url is a display preview; the printable source resolver, the bridge, the
 * manifest and the print agent read `print-source-resolver.ts` and the storage
 * probe, never these fields. Which SIDES this job prints is the station's own
 * call (`sideState`, HEAD probed or read off the manifest) and it drives the
 * Print buttons on its own: the filter below READS that call and can only ever
 * remove a picture, so a mockup appearing here can never add a side to the job
 * and a missing mockup can never remove one. The dependency runs one way —
 * tiles follow the side state, never the reverse.
 *
 * SIZING. `comparison`: the strip FILLS the card's preview column, the whole
 * width left of the `lg:w-72` identity block, and the shared primitive alone
 * decides how. One tile takes the column and gets a 2:1 frame so both halves
 * of a custom's combined preview stay legible; two tiles split it into equal
 * tracks, so a standard's Front and Back are the SAME SIZE as each other
 * ("they can be labelled, both same size"). At the page's 1280px cap that is
 * an 878x439 custom or a pair of 433px squares, against the 448x224 / 224x224
 * the fixed `hero` gave them.
 *
 * It costs height: ~463px with the caption at that width, against the ~567px
 * of room above the print bar on a 768px station screen, so the card still
 * fits — and the print actions are `sticky bottom-0` regardless, so a taller
 * card can only ever cost a scroll to reach Report Issue, never put the Print
 * buttons out of reach. Brett was shown the capped and uncapped arithmetic
 * (2026-09-22) and chose "Literally full width, no cap": at a 224px cap the
 * frames get wider but `object-contain` still sizes the image off the height,
 * so a custom would have rendered at exactly the size he asked us to grow.
 */
import { useMemo } from 'react';
import {
  ProductionPreviewStrip,
  buildFinishedGarmentPreviewEntries,
  buildProductionLightboxData,
  type ProductionLightboxData,
  type ProductionPreviewEntry,
  type ProductionPreviewFacts,
  type ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';

/**
 * PURE. The Print card's entries: the shared garment policy, minus any side
 * tile for a side THIS JOB DOES NOT PRINT.
 *
 * THE ONE DEFINITION. The strip below and `buildPrintLightboxData` both read
 * it, because two copies of a filter is two chances for the card and the
 * lightbox to disagree about what exists — and that disagreement is a tap
 * opening somebody else's image, the exact failure the shared key/index
 * machinery was built to make impossible.
 *
 * `hasFront` / `hasBack` are the station's AUTHORITATIVE side flags (HEAD
 * probed or read off the manifest), the same pair `sideState` and the Print
 * buttons are derived from. Reading them here is what keeps the tiles and the
 * banner telling the operator one story.
 *
 * `product` and `productPreview` pass through untouched: they claim no side,
 * so no side state can contradict them. A custom keeps its single combined
 * tile on a back-only job exactly as it did before.
 *
 * MODULE-PRIVATE on purpose. It is not a policy any other station should
 * import: the other three want the finished garment's other side.
 */
function buildPrintStationPreviewEntries(
  facts: ProductionPreviewFacts,
): ProductionPreviewEntry[] {
  const kept = buildFinishedGarmentPreviewEntries(facts).filter((entry) => {
    if (entry.kind === 'front') return facts.hasFront;
    if (entry.kind === 'back') return facts.hasBack;
    return true;
  });
  if (kept.length > 0) return kept;

  // The filter emptied the strip — a front mockup on a back-only job, say. Ask
  // the shared builder for the SAME no-side tile it hands a garment with no
  // mockups on file at all, rather than restating that tile here where it
  // could drift from it. The card keeps its preview block and its footprint,
  // and still identifies the product it is about to print on.
  return buildFinishedGarmentPreviewEntries({
    ...facts,
    frontMockupPreviewUrl: null,
    backMockupPreviewUrl: null,
  });
}

/**
 * PURE. The card's flat lightbox list, built from the SAME entries the tiles
 * render from, so the rendered tiles and the key map can never disagree about
 * what exists. Tiles with no source at all are left out: there is nothing to
 * open, and they are not clickable.
 */
export function buildPrintLightboxData(args: {
  garmentUnitId: string;
  subject: string;
  facts: ProductionPreviewFacts;
}): ProductionLightboxData {
  const { garmentUnitId, subject, facts } = args;
  return buildProductionLightboxData([
    {
      subjectId: garmentUnitId,
      subject,
      entries: buildPrintStationPreviewEntries(facts),
    },
  ]);
}

export interface PrintStationPreviewsProps {
  /** The garment's preview facts, straight off the lookup response. */
  facts: ProductionPreviewFacts;
  /** What the images are OF: the cleaned product name. Must not be blank. */
  subject: string;
  /** Opens the card's lightbox. `sourceIndex` is the chain position that
   *  ACTUALLY rendered in the tile the operator tapped. */
  onOpen?: (kind: ProductionPreviewKind, sourceIndex: number) => void;
}

export function PrintStationPreviews({ facts, subject, onOpen }: PrintStationPreviewsProps) {
  const entries = useMemo(() => buildPrintStationPreviewEntries(facts), [facts]);

  return (
    // No shape prop, and no sizing classes: on a fill variant the shape is the
    // tile COUNT, which the strip knows and this component does not need to.
    // A custom's one combined preview and a one-sided standard's single tile
    // therefore come out identical, which is what Brett asked for.
    <ProductionPreviewStrip
      entries={entries}
      subject={subject}
      variant="comparison"
      onOpen={onOpen}
    />
  );
}
